import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { StatusBadge } from "@/components/shared/StatusBadge";
import {
  darDeBajaAccountantClient,
  reactivarAccountantClient,
} from "@/services/dashboardService";

/**
 * Accountant-side card to dar de baja / reactivar a cartera client (offboarding).
 * Rendered on the accountant client-detail "Perfil fiscal" tab. The backend
 * endpoints are membership-gated (the accountant must have a TenantMembership on
 * this tenant). Baja stops reportes/recibidas/emisión + pauses billing; data is
 * retained. `bajaAt`/`bajaReason` come from the page's tenant-detail query.
 */
export function AccountantBajaCard({
  tenantId,
  bajaAt,
  bajaReason,
}: {
  tenantId: string;
  bajaAt: string | null;
  bajaReason: string | null;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reason, setReason] = useState("");

  const invalidate = (): void => {
    void queryClient.invalidateQueries({
      queryKey: ["accountant", "tenant-detail", tenantId],
    });
  };

  const bajaMutation = useMutation({
    mutationFn: () => darDeBajaAccountantClient(tenantId, reason.trim()),
    onSuccess: () => {
      toast.success("Cliente dado de baja.");
      setConfirmOpen(false);
      setReason("");
      invalidate();
    },
    onError: () => toast.error("No se pudo dar de baja."),
  });

  const reactivarMutation = useMutation({
    mutationFn: () => reactivarAccountantClient(tenantId),
    onSuccess: () => {
      toast.success("Cliente reactivado.");
      invalidate();
    },
    onError: () => toast.error("No se pudo reactivar."),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          Baja de cliente
          {bajaAt ? (
            <StatusBadge variant="danger">Dado de baja</StatusBadge>
          ) : null}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {bajaAt ? (
          <>
            <p className="text-sm text-muted-foreground">
              Dado de baja el {new Date(bajaAt).toLocaleDateString("es-UY")}.
              {bajaReason ? ` Motivo: ${bajaReason}` : ""}
            </p>
            <p className="text-sm text-muted-foreground">
              No se envían reportes diarios ni se sincronizan recibidas, y no se
              pueden emitir comprobantes. Los datos se conservan.
            </p>
            <Button
              variant="outline"
              onClick={() => reactivarMutation.mutate()}
              disabled={reactivarMutation.isPending}
            >
              Reactivar cliente
            </Button>
          </>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              Dar de baja detiene reportes diarios, recibidas y emisión, y pausa
              la facturación (plan gratis). Los datos se conservan — no se elimina
              nada. Usalo cuando el RUC fue dado de baja en DGI.
            </p>
            <Button variant="destructive" onClick={() => setConfirmOpen(true)}>
              Dar de baja
            </Button>
          </>
        )}
      </CardContent>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Dar de baja al cliente</DialogTitle>
            <DialogDescription>
              Se detienen reportes, recibidas y emisión, y se pausa la
              facturación. Los datos se conservan. Ingresá el motivo (opcional).
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Motivo (ej.: RUC dado de baja en DGI)"
            rows={3}
          />
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => bajaMutation.mutate()}
              disabled={bajaMutation.isPending}
            >
              Confirmar baja
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
